/**
 * What `/fly`'s boot is made of, as a phase list with **measured** durations.
 *
 * A progress bar is a claim about how much of the wait is left, and the only
 * honest way to make that claim is to measure. `BOOT_PHASE_MS` is milliseconds
 * off real boots of this application — the runs and the conditions are tabulated
 * in `docs/engineering/fly-boot-timing.md` — and everything else here is derived
 * from them. Nothing in this module animates on a clock of its own invention.
 *
 * ## Why a phase needs a duration and not just a share
 *
 * The phases are wildly uneven and their unevenness *moves with the network*:
 * on a warm cache the airframe is 30 ms and on a cold 20 Mbit link it is 730 ms,
 * while the pipeline precompile is 570 ms in every condition measured because it
 * is compute. A bar segmented by share alone would therefore sit still for
 * two-thirds of a cold boot and then leap. So each phase carries an **expected
 * duration**, and the bar eases across that phase's own share on the curve
 * `1 − e^(−k·t/expected)` — which approaches the phase's end without ever
 * reaching it, so a phase that runs long can never claim to have finished, and
 * the boundary is only crossed when the boot really does move on.
 *
 * ## Why these numbers and not the warm ones
 *
 * They are taken from the **cold** 20 Mbit profile, averaged over the ENHANCED
 * MODELS on and off runs, because that is the boot a loading screen exists for:
 * a warm boot is over in 1.3 s and the screen is a flicker either way. The two
 * runs disagree most about `airframe` and `region` — with the boot's fetches now
 * running concurrently (`scene-boot.ts`), whichever of them the thread is inside
 * when the region's synchronous build lands absorbs it — so their shares are the
 * mean of the two rather than either one, and no phase is left at zero.
 *
 * Nothing here imports three.js, so the phase model is testable without a GPU.
 */

/**
 * One step of `scene-boot.ts`'s `watchInit`, reported as it *starts* — so the
 * label always names what the boot is now waiting for.
 *
 * `device` covers `renderer.init()` and the first viewport sizing; `guns` the
 * cockpit's muzzle table; `airframe` the player's own aeroplane (the enhanced
 * `.glb` when ENHANCED MODELS is on — the single largest fetch a boot makes);
 * `propellers` AD-012's discs; `region` the `.WLD` body, its ~50 placed models,
 * the relief field and the instanced scenery; `terrain` draping that relief into
 * the scene; and `precompile` every WGSL pipeline, so the first drawn frame does
 * not compile one on the main thread (`three/precompile-scene.ts`).
 *
 * The middle three are *started together* and only awaited in this order, so a
 * label here names the outstanding wait rather than the exclusive occupant of
 * the thread. That is deliberate and it is what made the cold boot 11 % shorter.
 */
export type BootPhase = "device" | "guns" | "airframe" | "propellers" | "region" | "terrain" | "precompile";

/** The phases in the order `watchInit` reports them. */
export const BOOT_PHASES: readonly BootPhase[] = ["device", "guns", "airframe", "propellers", "region", "terrain", "precompile"];

/**
 * Milliseconds each phase is expected to take, from the reference cold 20 Mbit
 * runs. Used for two different things — the *share* of the bar a phase owns, and
 * the rate the bar eases across it — which is why they are durations rather than
 * normalised weights.
 *
 * `propellers` and `terrain` are genuinely 1 ms once `BARON.PAL` is in hand and
 * are recorded as 5 rather than padded to something "visible": a step that costs
 * nothing should take none of the bar. They are floored at 5 only so no expected
 * duration is zero, which the easing divides by.
 */
export const BOOT_PHASE_MS: Readonly<Record<BootPhase, number>> = {
  device: 10,
  guns: 430,
  airframe: 430,
  propellers: 5,
  region: 220,
  terrain: 5,
  precompile: 580,
};

const TOTAL_MS = BOOT_PHASES.reduce((sum, phase) => sum + BOOT_PHASE_MS[phase], 0);

/**
 * How fast the bar eases across a phase's share. At `t = expected` the segment
 * is `1 − e^−2` = 86 % filled, so a phase that runs to its measured length looks
 * nearly done and one that overruns keeps creeping instead of stopping dead.
 */
const EASE_RATE = 2;

/**
 * The most of a segment easing may ever claim.
 *
 * `1 − e^(−k·t)` is asymptotic in arithmetic and **not** in floating point: past
 * about 20 time constants `e^(−k·t)` underflows to zero and the expression
 * returns exactly 1, which would put the bar hard against the next boundary —
 * and for the last phase, hard against 100 % — while the screen is still black.
 * A test caught it at ten minutes inside a phase. So the easing is capped: the
 * final 1 % of a segment is never claimed by the clock, only by the boot
 * actually moving on.
 */
const EASE_CAP = 0.99;

/**
 * How much of a phase's segment its **measured bytes** may claim.
 *
 * A phase is a fetch plus the work that follows it, and the bytes say nothing
 * about the work: `airframe` is a 1.4 MB `.glb` and then a `GLTFLoader` parse, a
 * repaint and a scene mutation. So a fully-arrived download moves the bar to
 * four fifths of the phase and no further, and the remaining fifth is left to
 * the easing and to the boot actually moving on.
 *
 * Below `EASE_CAP`, which keeps the one invariant this module has: nothing but
 * the next phase starting may cross a phase boundary.
 */
const BYTES_SHARE = 0.8;

/**
 * How far past its expected duration a phase runs before the screen starts
 * saying so, as a multiple of the expectation.
 *
 * Two, because one is the *mean* — half of all healthy boots are slower than
 * their expected duration by construction, and a caption that appeared on half
 * of all boots would be noise rather than information.
 */
export const BOOT_OVERRUN_FACTOR = 2;

/**
 * The floor under that threshold, and the reason there is one: `propellers` and
 * `terrain` expect 5 ms, so twice their expectation is 10 ms and the caption
 * would fire on every boot ever taken. Four seconds is the point at which a
 * wait stops reading as a load and starts reading as a hang, which is exactly
 * when the screen owes the player an explanation.
 */
export const BOOT_OVERRUN_FLOOR_MS = 4_000;

/** How much of the current phase's dominant fetch has arrived. `total` of zero means "no signal". */
export interface BootBytes {
  readonly loaded: number;
  readonly total: number;
}

/**
 * The share of the whole boot that is complete when `phase` is **about to
 * start** — the sum of every earlier phase's expected duration, normalised.
 *
 * `bootProgressAt("device")` is therefore 0, and the bar is full only when the
 * scene reports `ready`, which is a separate signal rather than an eighth phase:
 * a bar that reaches 100 % while the picture is still black is the lie this
 * module exists to avoid.
 */
export function bootProgressAt(phase: BootPhase): number {
  let done = 0;
  for (const candidate of BOOT_PHASES) {
    if (candidate === phase) break;
    done += BOOT_PHASE_MS[candidate];
  }
  return done / TOTAL_MS;
}

/** The share of the bar `phase` owns. */
export function bootProgressSpan(phase: BootPhase): number {
  return BOOT_PHASE_MS[phase] / TOTAL_MS;
}

/**
 * Where the bar stands `elapsedMs` into `phase`, given whatever is known about
 * the bytes still on the wire.
 *
 * Strictly inside `[start, start + span)`: it starts at the phase's own boundary
 * and approaches the next one asymptotically, so however long a phase takes the
 * bar never crosses into the next phase's territory and never reads as complete.
 *
 * `bytes` is the honest half. The easing is a *guess* calibrated on a mean, and
 * on the boot where the guess is worst — a slow link, where `airframe`'s 1.4 MB
 * takes seven seconds rather than four hundred milliseconds — the download is
 * the one thing about the wait that is actually measurable. Where it is known it
 * wins, because a measurement beats a curve; where it is not, or where the
 * easing has already run further than the download has, the curve carries the
 * phase as it always did. Whichever is in front, the bar only ever moves
 * forward.
 */
export function bootBarFraction(phase: BootPhase, elapsedMs: number, bytes?: BootBytes): number {
  const start = bootProgressAt(phase);
  const span = bootProgressSpan(phase);
  const t = Math.max(0, elapsedMs) / BOOT_PHASE_MS[phase];
  const eased = span * Math.min(EASE_CAP, 1 - Math.exp(-EASE_RATE * t));
  const measured = bytes && bytes.total > 0 ? span * BYTES_SHARE * Math.min(1, Math.max(0, bytes.loaded / bytes.total)) : 0;
  return start + Math.max(eased, measured);
}

/** The point past which `phase` has blown its budget and the caption starts explaining itself. */
export function bootOverrunAfterMs(phase: BootPhase): number {
  return Math.max(BOOT_PHASE_MS[phase] * BOOT_OVERRUN_FACTOR, BOOT_OVERRUN_FLOOR_MS);
}

/**
 * The port's own words for the port's phases. `RB.EXE` shows no per-phase
 * loading text — it paints a briefing and swaps to the cockpit — so none of
 * these is transcribed from a string table. Each one describes what that step
 * actually does, in the shell's telegraphic upper case: the guns line really is
 * the gun-mount table, and the survey line really is the sector's `.WLD`.
 */
export const BOOT_PHASE_LABELS: Readonly<Record<BootPhase, string>> = {
  device: "STARTING THE ENGINE",
  guns: "LOADING THE GUNS",
  airframe: "ROLLING OUT THE AIRCRAFT",
  propellers: "FITTING THE PROPELLER",
  region: "SURVEYING THE SECTOR",
  terrain: "MAPPING THE GROUND",
  precompile: "PRE-FLIGHT CHECKS",
};

/**
 * The caption under the heading: the phase's own words, and — once the phase has
 * run past `bootOverrunAfterMs` — how long it has been running.
 *
 * **The seconds are the whole point of this function.** By construction the bar
 * cannot move once a phase passes about five of its time constants: the easing
 * is capped at 99 % of the segment, so a phase that blows its budget parks a
 * static bar against a boundary and says nothing, which is exactly what "stuck
 * at 99 % for almost a minute" was (user report, 2026-08-26). A ticking count is
 * the honest thing to put there — it is real, it is motion, it names the step
 * that is taking the time, and it claims no progress that has not happened.
 *
 * Set in the shell's own telegraphic upper case, with a hyphen and an `S`
 * because both are in `FONT3` and neither needs a glyph the bake does not ship.
 */
export function bootPhaseCaption(phase: BootPhase, elapsedMs: number): string {
  const label = BOOT_PHASE_LABELS[phase];
  if (elapsedMs < bootOverrunAfterMs(phase)) return label;
  return `${label} - ${Math.floor(elapsedMs / 1000)}S`;
}

/** What the overlay is handed each time the boot moves on. */
export interface BootProgress {
  readonly phase: BootPhase;
  /** 0…1, the share of the boot already done when this phase started. */
  readonly fraction: number;
  readonly label: string;
  /**
   * The phase's dominant download, where it has one that can report itself.
   * Only `airframe` does today — the enhanced `.glb` is the largest single thing
   * a boot fetches (`docs/engineering/fly-boot-timing.md` §6) — and a report
   * carrying it is a *re-report of the same phase*, not a new one.
   */
  readonly bytes?: BootBytes;
  /**
   * A standing fact about this boot rather than a step in it — printed under the
   * bar and, unlike the phase caption, carried unchanged through every report
   * that follows the one which set it.
   *
   * There is exactly one today: `RENDERER: WEBGL` when the flight did not come
   * up on WebGPU (`three/renderer-mode.ts`). It is deliberately absent on the
   * ordinary path, so the loading screen a player sees every sortie is the one
   * it has always been and the line appears only when there is something to say.
   */
  readonly note?: string;
}

/** The report for a phase that is starting now, or a fresh byte count for the one already running. */
export function bootProgressFor(phase: BootPhase, bytes?: BootBytes, note?: string): BootProgress {
  return { phase, fraction: bootProgressAt(phase), label: BOOT_PHASE_LABELS[phase], bytes, note };
}
